/**
 * Feb 2026 R293zq · Item Catalog picker (multi-select with qty).
 *
 * Full-width Shadcn dialog opened from the Composer to pick line items
 * from the central Item Catalog (`/workspaces/{ws}/items`). Each
 * selected row gets its own qty input so the user composes an entire
 * quotation in one shot.
 *
 * Fields carried into the Composer line:
 *   • name + description  → merged into line description
 *   • rate                → line rate
 *   • currency            → line currency (for info only)
 *   • hsn_sac             → line HSN/SAC
 *   • tax_pct             → line gst_rate
 *   • unit                → line unit (UOM)
 *   • qty                 → line qty (user-set, defaults to 1)
 *
 * The full catalog CRUD page lives at `/app/items`. This dialog is a
 * read-only picker on top of the same data.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { apiErrorMessage } from "@/lib/apiError";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Search, Check, Package, ExternalLink, Paperclip, ChevronDown, ChevronRight } from "lucide-react";
import CatalogSpecThumb from "./CatalogSpecThumb";

export default function CatalogDialog({
  open, onClose, ws, onPickMany,
}) {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");
  // selected: Map<itemId, qty>
  const [selected, setSelected] = useState({});
  // Feb 2026 R293zz · Rows the user expanded to preview spec sheets.
  const [specsOpen, setSpecsOpen] = useState({});

  const load = useCallback(async () => {
    if (!ws || !open) return;
    setLoading(true);
    try {
      const params = { active_only: true };
      if (q.trim()) params.q = q.trim();
      const r = await api.get(`/workspaces/${ws}/items`, { params });
      setRows(r.data || []);
    } catch (e) { toast.error(apiErrorMessage(e, "Failed to load catalog")); }
    finally { setLoading(false); }
  }, [ws, open, q]);

  // Debounced reload on query change
  useEffect(() => {
    if (!open) return;
    const t = setTimeout(load, 220);
    return () => clearTimeout(t);
  }, [load, open]);

  useEffect(() => {
    if (!open) setSelected({});
  }, [open]);

  const toggleSelect = (row) => {
    setSelected((prev) => {
      const next = { ...prev };
      if (next[row.id] != null) delete next[row.id];
      else next[row.id] = 1;
      return next;
    });
  };

  const setQty = (id, v) => {
    const n = Number(v);
    setSelected((prev) => ({ ...prev, [id]: Number.isFinite(n) && n > 0 ? n : "" }));
  };

  const selectedCount = Object.keys(selected).length;

  const addSelected = () => {
    const chosen = rows
      .filter((r) => selected[r.id] != null)
      .map((r) => ({ ...r, qty: Number(selected[r.id]) || 1 }));
    if (!chosen.length) { toast.info("Tick at least one item first"); return; }
    onPickMany?.(chosen);
    setSelected({});
    onClose?.();
  };

  const filtered = useMemo(() => rows, [rows]);

  if (!open) return null;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent
        className="w-screen max-w-full h-[100dvh] max-h-[100dvh] rounded-none border-0 overflow-hidden p-0 sm:max-w-[96vw] sm:h-[92vh] sm:rounded-lg"
        data-testid="catalog-dialog"
      >
        <div className="flex flex-col h-full">
          <DialogHeader className="px-5 py-3 border-b border-border">
            <DialogTitle className="flex items-center gap-2">
              <Package className="h-5 w-5 text-primary" />
              Pick from catalog
              <span className="text-xs font-normal text-muted-foreground">
                {rows.length} item{rows.length === 1 ? "" : "s"}
              </span>
            </DialogTitle>
            <DialogDescription className="text-xs">
              Tick rows, set a quantity, then click <b>Add to document</b>.
              Rate, HSN, tax % and unit are pulled in automatically — you can
              still tweak them on the line after.
            </DialogDescription>
          </DialogHeader>

          {/* Toolbar */}
          <div className="px-5 py-2 border-b border-border flex items-center gap-2 flex-wrap bg-muted/20">
            <div className="relative flex-1 min-w-[220px]">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
              <Input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search name, SKU, category…"
                className="pl-7 h-8 text-xs"
                data-testid="catalog-search"
                autoFocus
              />
            </div>
            <Button
              size="sm" variant="outline"
              onClick={() => navigate("/app/items")}
              data-testid="catalog-manage-btn"
              className="h-8 text-xs"
              title="Open the full catalog page to add or edit items"
            >
              <ExternalLink className="h-3.5 w-3.5 mr-1" /> Manage catalog
            </Button>
            <Button
              size="sm"
              onClick={addSelected}
              disabled={selectedCount === 0}
              data-testid="catalog-add-selected-btn"
              className="h-8 text-xs"
            >
              <Check className="h-3.5 w-3.5 mr-1" />
              Add to document ({selectedCount})
            </Button>
            <Button
              size="sm" variant="ghost"
              onClick={onClose}
              data-testid="catalog-close-btn"
              className="h-8 text-xs"
            >
              Close
            </Button>
          </div>

          {/* Table */}
          <div className="flex-1 overflow-auto">
            <table className="w-full text-xs" data-testid="catalog-table">
              <thead className="bg-muted sticky top-0 z-10 text-[10px] uppercase tracking-wider text-muted-foreground">
                <tr>
                  <th className="px-3 py-2 w-10"></th>
                  <th className="px-3 py-2 text-left w-28">SKU</th>
                  <th className="px-3 py-2 text-left">Name</th>
                  <th className="px-3 py-2 text-left w-32">Category</th>
                  <th className="px-3 py-2 text-left w-20">Unit</th>
                  <th className="px-3 py-2 text-right w-28">Rate</th>
                  <th className="px-3 py-2 text-right w-16">Tax %</th>
                  <th className="px-3 py-2 text-left w-24">HSN/SAC</th>
                  <th className="px-3 py-2 text-right w-24">Qty</th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr><td colSpan={9}
                          className="text-center py-6 text-muted-foreground">
                    Loading catalog…
                  </td></tr>
                )}
                {!loading && filtered.length === 0 && (
                  <tr><td colSpan={9}
                          className="text-center py-8 text-muted-foreground">
                    No items match. Open <b>Manage catalog</b> to add one.
                  </td></tr>
                )}
                {filtered.map((r, idx) => {
                  const isSel = selected[r.id] != null;
                  const csym = r.currency === "INR" ? "₹" : (r.currency || "") + " ";
                  return (
                    <tr
                      key={r.id}
                      className={"border-t border-border hover:bg-primary/5 cursor-pointer " +
                        (idx % 2 ? "bg-muted/20 " : "") +
                        (isSel ? "bg-primary/5 " : "")}
                      data-testid={`catalog-row-${r.id}`}
                      onClick={() => toggleSelect(r)}
                    >
                      <td className="px-3 py-2 text-center" onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={isSel}
                          onChange={() => toggleSelect(r)}
                          className="h-4 w-4"
                          data-testid={`catalog-select-${r.id}`}
                        />
                      </td>
                      <td className="px-3 py-2 font-mono text-muted-foreground">
                        {r.sku || "—"}
                      </td>
                      <td className="px-3 py-2">
                        <div className="font-medium flex items-center gap-1.5">
                          {r.name}
                          {Array.isArray(r.spec_attachments) && r.spec_attachments.length > 0 && (
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                setSpecsOpen((prev) => ({ ...prev, [r.id]: !prev[r.id] }));
                              }}
                              className="inline-flex items-center gap-0.5 px-1 py-[1px] rounded bg-amber-100 text-amber-700 text-[10px] font-semibold hover:bg-amber-200"
                              title={`${r.spec_attachments.length} spec sheet(s) · click to preview — all auto-copy to this document`}
                              data-testid={`catalog-row-specs-${r.id}`}
                            >
                              {specsOpen[r.id] ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
                              <Paperclip className="h-3 w-3" />{r.spec_attachments.length}
                            </button>
                          )}
                        </div>
                        {r.description && (
                          <div className="text-[11px] text-muted-foreground truncate max-w-xl">
                            {r.description}
                          </div>
                        )}
                        {/* Feb 2026 R293zz · Spec thumbnails — only
                            fetched when the badge is expanded. */}
                        {specsOpen[r.id] && Array.isArray(r.spec_attachments) && r.spec_attachments.length > 0 && (
                          <div
                            className="mt-2 flex flex-wrap gap-2"
                            onClick={(e) => e.stopPropagation()}
                            data-testid={`catalog-row-specs-preview-${r.id}`}
                          >
                            {r.spec_attachments.map((att) => (
                              <CatalogSpecThumb key={att.id} itemId={r.id} att={att} />
                            ))}
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-2 text-muted-foreground">
                        {r.category || "—"}
                      </td>
                      <td className="px-3 py-2 text-muted-foreground">
                        {r.unit || "unit"}
                      </td>
                      <td className="px-3 py-2 text-right font-mono tabular-nums">
                        {csym}{Number(r.rate || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}
                      </td>
                      <td className="px-3 py-2 text-right font-mono tabular-nums">
                        {r.tax_pct ?? 0}%
                      </td>
                      <td className="px-3 py-2 font-mono text-muted-foreground">
                        {r.hsn_sac || "—"}
                      </td>
                      <td className="px-3 py-2 text-right" onClick={(e) => e.stopPropagation()}>
                        {isSel ? (
                          <input
                            type="number" min="0" step="any"
                            value={selected[r.id]}
                            onChange={(e) => setQty(r.id, e.target.value)}
                            className="w-20 text-right text-xs px-2 py-1 bg-background border border-border tabular-nums focus:border-primary focus:outline-none"
                            data-testid={`catalog-qty-${r.id}`}
                            autoFocus
                          />
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
